import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ShieldCheck,
  Film,
  Tags,
  Inbox,
  ClipboardList,
  Users,
  Home,
  FileEdit,
} from "lucide-react";
import { StarsBackground } from "@/components/StarsBackground";
import { useAuth } from "@/hooks/useAuth";

import ClipsTab from "./admin/ClipsTab";
import PricingTab from "./admin/PricingTab";
import ContentTab from "./admin/ContentTab";
import OrdersTab from "./admin/OrdersTab";
import FormSetupTab from "./admin/FormSetupTab";
import UsersTab from "./admin/UsersTab";

const TabBtn = ({ active, onClick, icon, children, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className={`relative px-5 py-3 text-sm font-display uppercase tracking-widest flex items-center gap-2 transition ${
      active ? "text-white" : "text-white/45 hover:text-white/75"
    }`}
  >
    {icon}
    {children}
    {active && <span className="absolute left-0 right-0 -bottom-px h-0.5" style={{ background: "var(--rv-red)" }} />}
  </button>
);

export default function Admin() {
  const { me, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("clips");

  if (authLoading) {
    return (
      <div className="relative min-h-screen flex items-center justify-center">
        <StarsBackground />
        <div className="text-white/40 text-sm">Loading…</div>
      </div>
    );
  }

  if (!me) {
    return (
      <div className="relative min-h-screen flex items-center justify-center px-6">
        <StarsBackground />
        <div className="relative z-10 max-w-md w-full rv-card rounded-xl p-8 text-center" data-testid="admin-needs-login">
          <ShieldCheck className="mx-auto" size={36} style={{ color: "var(--rv-red)" }} />
          <h1 className="font-display font-black uppercase text-2xl mt-4">Admin Access</h1>
          <p className="text-white/55 text-sm mt-2">Please sign in with Discord first.</p>
          <Link to="/" className="btn-primary mt-6 justify-center w-full" data-testid="admin-go-home">
            <Home size={14} /> Back to home
          </Link>
        </div>
      </div>
    );
  }

  if (!me.is_admin) {
    return (
      <div className="relative min-h-screen flex items-center justify-center px-6">
        <StarsBackground />
        <div className="relative z-10 max-w-md w-full rv-card rounded-xl p-8 text-center" data-testid="admin-no-access">
          <ShieldCheck className="mx-auto" size={36} style={{ color: "var(--rv-red)" }} />
          <h1 className="font-display font-black uppercase text-2xl mt-4">Access denied</h1>
          <p className="text-white/55 text-sm mt-2">You&apos;re signed in, but your account isn&apos;t an admin.</p>
          <Link to="/" className="btn-primary mt-6 justify-center w-full">
            <Home size={14} /> Back to home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen pb-24" data-testid="admin-dashboard">
      <StarsBackground />
      <div className="relative z-10 max-w-6xl mx-auto px-6 md:px-10 pt-12">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <div className="section-label">Admin</div>
            <h1 className="font-display font-black uppercase text-4xl mt-2">Site Control</h1>
          </div>
          <button onClick={() => navigate("/")} className="btn-ghost" data-testid="admin-home-btn">
            <Home size={14} /> Back to home
          </button>
        </div>

        <div className="flex gap-2 mt-10 border-b border-white/10 overflow-x-auto">
          <TabBtn active={tab === "clips"} onClick={() => setTab("clips")} icon={<Film size={14} />} testid="tab-clips">
            Clips
          </TabBtn>
          <TabBtn active={tab === "pricing"} onClick={() => setTab("pricing")} icon={<Tags size={14} />} testid="tab-pricing">
            Pricing
          </TabBtn>
          <TabBtn active={tab === "content"} onClick={() => setTab("content")} icon={<FileEdit size={14} />} testid="tab-content">
            Value pages
          </TabBtn>
          <TabBtn active={tab === "orders"} onClick={() => setTab("orders")} icon={<Inbox size={14} />} testid="tab-orders">
            Orders
          </TabBtn>
          <TabBtn active={tab === "form"} onClick={() => setTab("form")} icon={<ClipboardList size={14} />} testid="tab-form">
            Form setup
          </TabBtn>
          <TabBtn active={tab === "users"} onClick={() => setTab("users")} icon={<Users size={14} />} testid="tab-users">
            Users
          </TabBtn>
        </div>

        <div className="mt-10">
          {tab === "clips" && <ClipsTab />}
          {tab === "pricing" && <PricingTab />}
          {tab === "content" && <ContentTab />}
          {tab === "orders" && <OrdersTab />}
          {tab === "form" && <FormSetupTab />}
          {tab === "users" && <UsersTab />}
        </div>
      </div>
    </div>
  );
}
